import { ProductFormBrand } from "../../../ProductForm.types";
import { useTranslation } from "react-i18next";
import { FormControl, Typography, useTheme } from "@mui/material";
import { FoodsSelectServerPagination } from "@foodpilot/foods";
import { isBrandCollectionError, useGetAllBrands } from "src/api/client-api/brand/useGetAllBrand";
import { BrandCollectionStateError } from "src/api/client-api/brand/BrandCollectionStateError";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { FoodsField, FoodsLoadingIndicator, FoodsServerSelectField } from "src/components/EntityForm/FoodsFields";

type BrandFieldProps = {
    brandIRI: IRI | null;
    setBrand: (newBrand: ProductFormBrand | null) => void;
    appearance?: "default" | "product-v2";
    label?: string;
    placeholder?: string;
};
export const BrandField = (props: BrandFieldProps) => {
    const { brandIRI, setBrand, appearance = "default", label, placeholder } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const isProductV2 = appearance === "product-v2";

    const { search, setSearch, delayedSearch } = useDelayedSearch();
    const {
        allBrands = [],
        brandCollectionState,
        refetch,
    } = useGetAllBrands({
        name: delayedSearch,
    });
    const isSearchFound = (value: string) => {
        if (delayedSearch === "") {
            return true;
        }

        return value.toLowerCase().includes(delayedSearch.toLowerCase());
    };

    if (!isProductV2) {
        const selectedBrand = allBrands.find((brand) => brand["@id"] === brandIRI) ?? null;

        return (
            <FormControl size="small">
                <Typography variant="body-semibold" sx={{ paddingBottom: theme.spacing(1) }}>
                    {t("marque")}
                </Typography>
                <FoodsSelectServerPagination
                    onChange={(data) => {
                        setBrand(data);
                    }}
                    options={allBrands}
                    selectedOption={selectedBrand}
                    getId={(item) => item["@id"]}
                    getName={(item) => item.name}
                    textOptions={{
                        placeholder: t("component.select.placeholder"),
                        searchPlaceholder: t("component.select.searchplaceholder"),
                        emptyOptionsText: t("component.select.nooptions"),
                        defaultValueText: t("component.select.defaultvalue"),
                        noResult: t("component.select.noresultfound"),
                    }}
                    search={search}
                    setSearch={setSearch}
                    isSearchFound={isSearchFound}
                    BoxProps={{ width: "75%" }}
                    BoxOptionsProps={{ width: theme.spacing(56.25) }}
                />
            </FormControl>
        );
    }

    const resolvedLabel = label ?? t("marque", "Brand");
    const resolvedPlaceholder = placeholder ?? t("component.select.placeholder", "Select an option");

    if (brandCollectionState.status === "loading") {
        return (
            <FoodsField label={resolvedLabel}>
                <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
            </FoodsField>
        );
    }

    if (isBrandCollectionError(brandCollectionState)) {
        return (
            <FoodsField label={resolvedLabel}>
                <BrandCollectionStateError state={brandCollectionState} onRetry={refetch} />
            </FoodsField>
        );
    }

    const selectedBrand = brandCollectionState.items.find((brand) => brand["@id"] === brandIRI) ?? null;

    return (
        <FoodsServerSelectField
            appearance="product-v2"
            label={resolvedLabel}
            onChange={(data) => {
                setBrand(data);
            }}
            options={brandCollectionState.items}
            selectedOption={selectedBrand}
            getId={(item) => item["@id"]}
            getName={(item) => item.name}
            placeholder={resolvedPlaceholder}
            searchPlaceholder={t("component.select.searchplaceholder")}
            emptyOptionsText={resolvedPlaceholder}
            defaultValueText={t("component.select.defaultvalue")}
            noResultText={t("component.select.noresultfound")}
            search={search}
            setSearch={setSearch}
            isSearchFound={isSearchFound}
        />
    );
};
